import React, { FC, useEffect, useState } from 'react';
import { find, get, groupBy, map } from 'lodash';
import {
  WorkPackage,
  IsoDocument,
  DeleteWorkPackageAttachmentPayload,
  DeleteWorkPackageAttachmentInput,
  DeleteWorkPackageAttachmentError,
  CrewMember,
} from '@bechtel/types';
import {
  Box,
  Text,
  SideSheet,
  SideSheetHeader,
  SideSheetBody,
  SideSheetClose,
  SideSheetContentCol,
  SideSheetSize,
  Tabs,
  TabProps,
  TabPanels,
  TabPanel,
  Spinner,
  SpinnerSize,
  useToastsDispatch,
  ToastType,
  ToastAction,
} from '@bechtel/gds';
import { GET_WORKPACKAGE_DOCUMENTS_BY_IDS, DELETE_WORK_PACKAGE_ATTACHMENT } from '@bechtel/client-gql';
import {
  formatBytes,
  TextWithEllipsis,
  TitleValue,
  DocumentList,
  DocumentViewer,
  useAppUser,
} from '@bechtel/client-common';
import { useQuery, useMutation, ApolloError } from '@apollo/client';
import {
  SideSheetDocumentHeaderStyled,
  TabListStyled,
  SideSheetContentColStyled,
  SubHeaderStyled,
  DocumentHeaderStyled,
  InfoContainerStyled,
} from './styled';
import { TabButtonStyled, TabStyled } from '~/detail/styled';
import { DrawingList } from './drawing-list';
import { t, Trans } from '@lingui/macro';
import { useWorkPackagePermissions } from '~/hooks/use-work-package-permissions';
import { useWorkPackage } from '../context';
import { DeleteDocumentModal, DeleteDocumentError } from '@bechtel/client-common';

export interface DocumentManagerProps {
  workPackageId: string;
  onClose: () => void;
  selectedTag?: string;
  selectedDrawingNo?: string;
}

interface DocumentProps {
  url?: string;
  documentNumber?: string;
  tagNumber?: string;
  documentId?: string;
  fileSize?: string;
  creator?: CrewMember;
}

const getTagNumber = (drawing: IsoDocument) => {
  return drawing.commodity?.materialCommodity ? drawing.commodity?.materialCommodity.tag : drawing.commodity?.weld?.tag;
};

const DocumentManager: FC<DocumentManagerProps> = ({ workPackageId, onClose, selectedTag, selectedDrawingNo }) => {
  const { assignedCrewMembers } = useWorkPackage();
  const { user } = useAppUser();
  const { canUploadDocument, canDeleteDocument } = useWorkPackagePermissions(assignedCrewMembers);
  const [selectedDocument, setSelectedDocument] = useState<DocumentProps | null>();
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [deleteDocumentError, setDeleteDocumentError] = useState<DeleteWorkPackageAttachmentError | ApolloError>();
  const { loading, error, data } = useQuery<{ workPackagesByIds: WorkPackage[] }>(GET_WORKPACKAGE_DOCUMENTS_BY_IDS, {
    variables: {
      ids: [workPackageId],
    },
    onCompleted: (data) => {
      if (!selectedDrawingNo) return;
      const drawings = data.workPackagesByIds[0]?.allDocuments;
      const selectedDrawing = find(drawings, (drawing) => drawing?.documentNumber === selectedDrawingNo);
      if (!selectedDrawing) return;
      setSelectedDocument({
        url: selectedDrawing.url,
        documentNumber: selectedDrawing.documentNumber,
        tagNumber: selectedDrawing.commodity?.materialCommodity
          ? selectedDrawing.commodity?.materialCommodity.tag
          : selectedDrawing.commodity?.weld?.tag,
      });
    },
  });
  const workPackage: WorkPackage | null = get(data, 'workPackagesByIds[0]');
  const documents = workPackage?.attachments;
  const drawings = workPackage?.allDocuments;
  const groupedDrawings = groupBy(drawings, (drawing) => drawing.documentNumber);
  const drawingNumbers = Object.keys(groupedDrawings);
  const dispatchToast = useToastsDispatch();
  const drawingList = map(drawingNumbers, (drawingNumber) => {
    return {
      drawingNumber: drawingNumber,
      associatedTagNumbers: map(groupedDrawings[drawingNumber], (drawing) => getTagNumber(drawing)),
    };
  });
  const [deleteDocument, { loading: deleteDocumentLoading }] = useMutation<
    { deleteWorkPackageAttachmentPayload: DeleteWorkPackageAttachmentPayload },
    { input: DeleteWorkPackageAttachmentInput }
  >(DELETE_WORK_PACKAGE_ATTACHMENT, {
    onCompleted: (data) => {
      const deleteWorkPackageAttachment = get(data, 'deleteWorkPackageAttachment');
      const deleteDocumentError = get(deleteWorkPackageAttachment, 'error');
      if (deleteDocumentError) {
        setDeleteDocumentError(deleteDocumentError);
      } else {
        const deleteDocumentToast: ToastAction = {
          type: 'ADD_TOAST',
          payload: {
            toastType: ToastType.SUCCESS,
            content: t`Document Deleted`,
            autoDismissTimeout: 5000,
          },
        };
        dispatchToast(deleteDocumentToast);
        setDeleteConfirmed(false);
        setSelectedDocument(null);
        setDeleted(true);

      }
    },
    onError: (error) => {
      setDeleted(false);
      setDeleteDocumentError(error);
    },
  });
  const onDeleteDocument = () => {
    setDeleted(false);
    deleteDocument({
      variables: {
        input: {
          attachmentId: selectedDocument?.documentId ? selectedDocument.documentId : '',
          workPackageId: workPackageId,
        },
      },
    });
  };
  const closeBlockErrorModal = () => {
    setDeleteDocumentError(null);
  };
  useEffect(() => {
    if (deleted && documents?.length == 0) {
      onClose();
    }
  }, [deleted, documents]);
  return (
    <SideSheet visible onDismiss={onClose} size={SideSheetSize.LARGE}>
      <SideSheetHeader title={t`Documents`}>
        <SideSheetClose onClick={onClose}></SideSheetClose>
      </SideSheetHeader>
      {loading && (
        <InfoContainerStyled
          css="height:100%;width:100%;"
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
        >
          <Box>
            <Spinner size={SpinnerSize.LARGE} />
          </Box>
        </InfoContainerStyled>
      )}
      {error && (
        <Box css="height: 100%;">
          <InfoContainerStyled>
            <Box pb={{ sm: '500' }} flexDirection={{ sm: 'column' }} alignItems={{ sm: 'center' }}>
              <Text>
                <Trans>There was a problem loading documents for this Work Package. Please try again later.</Trans>
              </Text>
            </Box>
          </InfoContainerStyled>
        </Box>
      )}

      {!loading && !error && workPackage && (
        <>
          <SubHeaderStyled>
            <TitleValue title={t`Work Package`} value={workPackage.tag}></TitleValue>
          </SubHeaderStyled>
          <SideSheetBody>
            {!selectedTag && (
              <SideSheetContentColStyled style={{ overflowY: 'hidden' }}>
                <SideSheetDocumentHeaderStyled>
                  <Trans> Documents</Trans> ({drawingNumbers?.length + (documents ? documents?.length : 0)})
                </SideSheetDocumentHeaderStyled>
                <Tabs>
                  <TabListStyled>
                    <TabStyled>
                      {({ selected }: TabProps) => {
                        return (
                          <TabButtonStyled selected={!!selected}>
                            <Text>
                              <Trans>Drawings</Trans> ({drawingNumbers?.length})
                            </Text>
                          </TabButtonStyled>
                        );
                      }}
                    </TabStyled>
                    <TabStyled>
                      {({ selected }: TabProps) => {
                        return (
                          <TabButtonStyled selected={!!selected}>
                            <Text>
                              <Trans>Other Documents</Trans> ({documents?.length})
                            </Text>
                          </TabButtonStyled>
                        );
                      }}
                    </TabStyled>
                  </TabListStyled>
                  <TabPanels>
                    <TabPanel css={{ outline: 'none' }}>
                      <DrawingList
                        drawings={drawingList}
                        onSelect={(drawing) => {
                          setSelectedDocument({
                            url: find(drawings, (document) => document.documentNumber === drawing.drawingNumber)?.url,
                            documentNumber: drawing.drawingNumber,
                            tagNumber: drawing.associatedTagNumbers.join(', '),
                          });
                        }}
                        selectedDrawingNo={selectedDocument?.documentNumber}
                      ></DrawingList>
                    </TabPanel>
                    <TabPanel css={{ outline: 'none' }}>
                      <DocumentList
                        documents={documents}
                        workPackageId={workPackageId}
                        canUploadDocument={canUploadDocument}
                        onSelect={(document) => {
                          setSelectedDocument({
                            url: document.url,
                            documentNumber: document.fileName,
                            documentId: document.id,
                            fileSize: document.fileSize,
                            creator: document.creator,
                          });
                        }}
                        selectedDocumentId={selectedDocument?.documentId}
                      ></DocumentList>
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </SideSheetContentColStyled>
            )}
            <SideSheetContentCol style={{ overflowY: 'hidden' }}>
              {selectedDocument && (
                <DocumentHeaderStyled>
                  <Box mb={{ sm: '400' }}>
                    <Text weight="600" size="500" color="grey.600">
                      {selectedDocument.documentNumber} &nbsp;
                      {selectedDocument.fileSize && `(${formatBytes(parseFloat(selectedDocument.fileSize))})`}
                    </Text>
                  </Box>
                  {selectedDocument?.tagNumber && (
                    <TextWithEllipsis color="grey.600" size="500">
                      <Trans>Tag</Trans>: {selectedTag ? selectedTag : selectedDocument?.tagNumber}
                    </TextWithEllipsis>
                  )}
                </DocumentHeaderStyled>
              )}
              <DocumentViewer
                url={selectedDocument?.url}
                deleting={deleteDocumentLoading}
                canDelete={canDeleteDocument && selectedDocument?.creator?.id === user?.id}
                onDelete={() => {
                  setDeleteConfirmed(true);
                }}
              ></DocumentViewer>
              {deleteConfirmed && (
                <DeleteDocumentModal
                  onDelete={() => onDeleteDocument()}
                  onCancel={() => setDeleteConfirmed(false)}
                  title={'Delete Document'}
                  textContent={`Are you sure you want to delete the document ${selectedDocument?.documentNumber} ?`}
                />
              )}
              {deleteDocumentError && (
                <DeleteDocumentError error={deleteDocumentError} onClose={closeBlockErrorModal} />
              )}
            </SideSheetContentCol>
          </SideSheetBody>
        </>
      )}
    </SideSheet>
  );
};

export default DocumentManager;
